import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionGenerationAdminPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    discipline: 'Physics',
    topic: '',
    subskills: '',
    cognitive_process: 'apply',
    difficulty: 'medium',
    n_pairs: 1,
    learning_outcomes: ''
  });
  
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError(null);
    setResult(null);
    
    try {
      // Parse subskills (comma-separated)
      const subskillsArray = formData.subskills.split(',').map(s => s.trim()).filter(s => s);
      
      if (subskillsArray.length === 0) {
        throw new Error(language === 'tr' ? 'En az bir beceri giriniz' : 'Please enter at least one skill');
      }
      
      // Parse learning outcomes (optional, comma-separated)
      const learningOutcomesArray = formData.learning_outcomes
        ? formData.learning_outcomes.split(',').map(o => o.trim()).filter(o => o)
        : null;
      
      const requestData = {
        discipline: formData.discipline,
        topic: formData.topic,
        subskills: subskillsArray,
        cognitive_process: [formData.cognitive_process],
        difficulty: formData.difficulty,
        n_pairs: parseInt(formData.n_pairs),
        learning_outcomes: learningOutcomesArray
      };
      
      // Generate pairs
      const response = await axios.post(
        `${API}/orchestrator/questions/generate-pairs`,
        requestData,
        { withCredentials: true }
      );
      
      setResult(response.data);
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleSaveToDB = async () => {
    if (!result || !result.pairs) return;
    
    try {
      const response = await axios.post(
        `${API}/orchestrator/questions/pairs-to-db`,
        result.pairs,
        { withCredentials: true }
      );
      
      alert(
        language === 'tr' 
          ? `✅ ${response.data.saved_count} soru başarıyla kaydedildi!`
          : `✅ ${response.data.saved_count} questions saved successfully!`
      );
      
      // Reset form
      setResult(null);
      setFormData({
        discipline: 'Physics',
        topic: '',
        subskills: '',
        cognitive_process: 'apply',
        difficulty: 'medium',
        n_pairs: 1,
        learning_outcomes: ''
      });
      
    } catch (err) {
      console.error('Save error:', err);
      alert(
        language === 'tr'
          ? `❌ Kayıt hatası: ${err.response?.data?.detail || err.message}`
          : `❌ Save error: ${err.response?.data?.detail || err.message}`
      );
    }
  };

  const text = {
    title: language === 'tr' ? 'Soru Üretimi - AI Agent' : 'Question Generation - AI Agent',
    subtitle: language === 'tr' ? 'TYT-AP Soru Çiftleri Oluştur' : 'Generate TYT-AP Question Pairs',
    discipline: language === 'tr' ? 'Disiplin' : 'Discipline',
    topic: language === 'tr' ? 'Konu' : 'Topic',
    topicPlaceholder: language === 'tr' ? 'örn: Newton Yasaları' : 'e.g. Newton\'s Laws',
    subskills: language === 'tr' ? 'Beceriler (virgülle ayırın)' : 'Skills (comma-separated)',
    subskillsPlaceholder: language === 'tr' ? 'kuvvet analizi, vektör toplama' : 'force analysis, vector addition',
    learningOutcomes: language === 'tr' ? 'Kazanımlar / Öğrenme Çıktıları' : 'Learning Outcomes',
    learningOutcomesPlaceholder: language === 'tr' ? 'Türev kavramını anlama ve uygulama, Değişim oranını hesaplama' : 'Understanding and applying derivatives, Calculating rate of change',
    learningOutcomesHelp: language === 'tr' ? 'Her kazanımı virgülle ayırın. Bu alan opsiyoneldir ve daha hedef odaklı sorular üretir.' : 'Separate each outcome with a comma. This field is optional and generates more targeted questions.',
    cognitive: language === 'tr' ? 'Bilişsel Süreç' : 'Cognitive Process',
    difficulty: language === 'tr' ? 'Zorluk' : 'Difficulty',
    nPairs: language === 'tr' ? 'Üretilecek Çift Sayısı' : 'Number of Pairs',
    generate: language === 'tr' ? 'Üret' : 'Generate',
    generating: language === 'tr' ? 'Üretiliyor...' : 'Generating...',
    saveDB: language === 'tr' ? 'Veritabanına Kaydet' : 'Save to Database',
    results: language === 'tr' ? 'Sonuçlar' : 'Results',
    pairsGenerated: language === 'tr' ? 'Üretilen Çift' : 'Pairs Generated',
    tytQuestion: language === 'tr' ? 'TYT Sorusu' : 'TYT Question',
    apQuestion: language === 'tr' ? 'AP Sorusu' : 'AP Question',
    bridgeComment: language === 'tr' ? 'Köprü Yorumu' : 'Bridge Comment',
    sharedSkills: language === 'tr' ? 'Paylaşılan Beceriler' : 'Shared Skills',
    learningOutcomesLabel: language === 'tr' ? 'Öğrenme Çıktıları' : 'Learning Outcomes'
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {text.title}
          </h1>
          <p className="text-gray-600">{text.subtitle}</p>
        </div>

        {/* Generation Form */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Discipline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.discipline}
                </label>
                <select
                  value={formData.discipline}
                  onChange={(e) => setFormData({...formData, discipline: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="Physics">Physics</option>
                  <option value="Chemistry">Chemistry</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Biology">Biology</option>
                </select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.topic}
                </label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({...formData, topic: e.target.value})}
                  placeholder={text.topicPlaceholder}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Subskills */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.subskills}
                </label>
                <input
                  type="text"
                  value={formData.subskills}
                  onChange={(e) => setFormData({...formData, subskills: e.target.value})}
                  placeholder={text.subskillsPlaceholder}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Learning Outcomes (Kazanımlar) */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.learningOutcomes} <span className="text-gray-500 text-xs">(Opsiyonel)</span>
                </label>
                <textarea
                  value={formData.learning_outcomes}
                  onChange={(e) => setFormData({...formData, learning_outcomes: e.target.value})}
                  placeholder={text.learningOutcomesPlaceholder}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 h-20"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">{text.learningOutcomesHelp}</p>
              </div>

              {/* Cognitive Process */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.cognitive}
                </label>
                <select
                  value={formData.cognitive_process}
                  onChange={(e) => setFormData({...formData, cognitive_process: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="remember">Remember</option>
                  <option value="apply">Apply</option>
                  <option value="analyze">Analyze</option>
                  <option value="justify">Justify</option>
                </select>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.difficulty}
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              {/* Number of Pairs */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {text.nPairs}
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.n_pairs}
                  onChange={(e) => setFormData({...formData, n_pairs: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={generating}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
                generating
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              }`}
            >
              {generating ? text.generating : text.generate}
            </button>
          </form>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>

        {/* Results */}
        {result && result.pairs && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {text.results} ({result.count} {text.pairsGenerated})
              </h2>
              <button
                onClick={handleSaveToDB}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-all"
              >
                💾 {text.saveDB}
              </button>
            </div>

            <div className="space-y-6">
              {result.pairs.map((pair, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-indigo-600 mb-4">
                    Pair {index + 1}: {pair.pair_id}
                  </h3>

                  {/* Learning Outcomes */}
                  {pair.learning_outcomes && pair.learning_outcomes.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">📚 {text.learningOutcomesLabel}:</p>
                      <div className="flex flex-wrap gap-2">
                        {pair.learning_outcomes.map((outcome, i) => (
                          <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                            {outcome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shared Skills */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">{text.sharedSkills}:</p>
                    <div className="flex flex-wrap gap-2">
                      {pair.shared_skills?.map((skill, i) => (
                        <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Bridge Comment */}
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-1">{text.bridgeComment}:</p>
                    <p className="text-sm text-gray-600">{pair.bridge_comment}</p>
                  </div>

                  {/* TYT Question */}
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-700 mb-2">🇹🇷 {text.tytQuestion}</p>
                    <p className="text-sm text-gray-700 mb-2">{pair.TYT_item?.stem_TR}</p>
                    {pair.TYT_item?.options?.map((opt, i) => (
                      <p key={i} className="text-sm text-gray-600 ml-4">{opt}</p>
                    ))}
                    <p className="text-sm text-green-600 mt-2">✓ {pair.TYT_item?.correct_option}</p>
                  </div>

                  {/* AP Question */}
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm font-semibold text-purple-700 mb-2">🇬🇧 {text.apQuestion}</p>
                    <p className="text-sm text-gray-700">{pair.AP_item?.stem_EN}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionGenerationAdminPage;
